import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserInfo } from '../api/auth';
import { useChatStore } from './chatStore';

interface AuthState {
  user: UserInfo | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: UserInfo) => void;
  logout: () => void;
  updateUser: (user: Partial<UserInfo>) => void;
  initAuth: () => void; // 初始化认证状态（从localStorage读取）
}

const STORAGE_KEY = 'auth_storage';
const TOKEN_KEY = 'token';
const REMEMBER_PASSWORD_KEY = 'remember_password';
const REMEMBER_PASSWORD_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7天
const GUEST_CHAT_COUNT_KEY = 'guest_chat_count';
const GUEST_CHAT_QUOTA_KEY = 'guest_chat_quota'; // 历史版本每日配额 key，登录时清理

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      login: (token: string, user: UserInfo) => {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem('user_info', JSON.stringify(user));
        // 登录后清除旧版未登录配额本地记录
        localStorage.removeItem(GUEST_CHAT_COUNT_KEY);
        localStorage.removeItem(GUEST_CHAT_QUOTA_KEY);
        set({ token, user, isAuthenticated: true });
        // 登录后清空旧对话并加载该用户的对话列表
        const { clearConversations, loadConversations } = useChatStore.getState();
        clearConversations();
        loadConversations();
      },
      logout: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('user_info');
        localStorage.removeItem(REMEMBER_PASSWORD_KEY);
        set({ user: null, token: null, isAuthenticated: false });
        // 登出后清空对话列表
        const { clearConversations } = useChatStore.getState();
        clearConversations();
      },
      updateUser: (updates) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, ...updates }
            : Object.keys(updates).length > 0
              ? { ...updates } as UserInfo
              : null,
        })),
      initAuth: () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          // 从localStorage恢复用户信息（如果有）
          const storedUser = localStorage.getItem('user_info');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              set({ token, user, isAuthenticated: true });
            } catch (e) {
              // 如果解析失败，只设置token，后续通过API获取用户信息
              set({ token, user: null, isAuthenticated: true });
            }
          } else {
            // 尝试从persist存储中恢复user
            const persistData = localStorage.getItem(STORAGE_KEY);
            if (persistData) {
              try {
                const parsed = JSON.parse(persistData);
                if (parsed.state?.user) {
                  set({ token, user: parsed.state.user, isAuthenticated: true });
                } else {
                  set({ token, user: null, isAuthenticated: true });
                }
              } catch (e) {
                set({ token, user: null, isAuthenticated: true });
              }
            } else {
              set({ token, user: null, isAuthenticated: true });
            }
          }
        }
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ user: state.user }), // 只持久化user，token单独存储
    }
  )
);

/**
 * 保存记住密码信息
 */
export const saveRememberPassword = (account: string, password: string) => {
  const data = {
    account,
    password,
    expiry: Date.now() + REMEMBER_PASSWORD_EXPIRY,
  };
  localStorage.setItem(REMEMBER_PASSWORD_KEY, JSON.stringify(data));
};

/**
 * 获取记住的密码信息
 */
export const getRememberPassword = (): { account: string; password: string } | null => {
  const data = localStorage.getItem(REMEMBER_PASSWORD_KEY);
  if (!data) return null;
  
  try {
    const parsed = JSON.parse(data);
    if (parsed.expiry && Date.now() < parsed.expiry) {
      return { account: parsed.account, password: parsed.password };
    } else {
      // 已过期，清除
      localStorage.removeItem(REMEMBER_PASSWORD_KEY);
      return null;
    }
  } catch (e) {
    return null;
  }
};

/**
 * 清除记住的密码
 */
export const clearRememberPassword = () => {
  localStorage.removeItem(REMEMBER_PASSWORD_KEY);
};

